import cn from 'classnames';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useResetRecoilState } from 'recoil';

import { InputV2 } from 'components/ComponentV2';
import { Form } from 'components/form';
import { Col, Row } from 'components/layout';

import { API_ROUTES } from 'lib/api-routes';
import { signUpSocietyState } from 'lib/atoms/signUpSocietyAtom';
import { TUserApiResponse } from 'lib/models/User.model';
import { useAuth } from 'lib/providers/AuthProvider';
import { post } from 'lib/utils/http';
import { parseErrorResponse, parseResponse } from 'lib/utils/parser';
import { OTP_RESEND_TIMER } from 'utils/format';
import { isManagerUserable } from 'utils/user';

import Header from '../Header';
import StepHeading from '../StepHeading';
import styles from './Step2.module.scss';

interface Step1Props {
  progress: number;
  setStep: (step: number) => void;
}

interface Inputs {
  otp1: string;
  otp2: string;
  otp3: string;
  otp4: string;
}

export const Step2: React.FC<Step1Props> = ({ progress, setStep }) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
    watch,
    reset: resetForm,
    setFocus,
  } = useForm();
  const { updateUserApiResponse } = useAuth();

  const [societyState] = useRecoilState(signUpSocietyState);
  const resetState = useResetRecoilState(signUpSocietyState);
  const { society, user } = societyState;

  const [t] = useTranslation('signup.manager');

  const [showError, setShowError] = React.useState<string>('');

  const otp1 = watch('otp1');
  const otp2 = watch('otp2');
  const otp3 = watch('otp3');
  const otp4 = watch('otp4');

  useEffect(() => {
    setFocus('otp1');
  }, [setFocus]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const body = {
      manager: {
        user_attributes: {
          email: user?.email,
          otp: data.otp1 + data.otp2 + data.otp3 + data.otp4,
        },
      },
    };

    try {
      const userApiResponse = await post<TUserApiResponse>(
        API_ROUTES.MANAGER_CONFIRM_EMAIL,
        body
      );
      const userApiAttributes = parseResponse(userApiResponse);
      if (isManagerUserable(userApiAttributes)) {
        updateUserApiResponse(userApiResponse);
        setStep(3);
        resetState();
      } else {
        setShowError(t('not-authorized'));
        setFocus('otp4');
      }
    } catch (error) {
      const message = parseErrorResponse(error as Error);
      setShowError(message);
    }
  };

  useEffect(() => {
    const subscription = watch((value) => {
      const { otp1, otp2, otp3, otp4 } = value;
      const otp: string = otp1 + otp2 + otp3 + otp4 || '';
      const optNumber = otp?.replace(/[^0-9]/g, '');
      if (optNumber.length === 4 && !isSubmitting) {
        handleSubmit(onSubmit)();
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const [seconds, setSeconds] = React.useState(OTP_RESEND_TIMER);
  const [reset, setReset] = React.useState(false);

  React.useEffect(() => {
    if (reset) {
      setSeconds(OTP_RESEND_TIMER);
      setReset(false);
    } else {
      const interval = setInterval(() => {
        setSeconds((prevSeconds) => {
          if (prevSeconds === 0) {
            clearInterval(interval);
          }
          return prevSeconds > 0 ? prevSeconds - 1 : 0;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [reset]);

  const handleReset = async () => {
    setReset(true);
    const data = {
      manager: {
        user_attributes: { email: user?.email },
        student_society_id: society?.id,
      },
    };
    await post(API_ROUTES.MANAGER_RESEND_CONFIRMATION, data);
    resetForm();
    setFocus('otp1');
  };

  const fillAllOtp = (value: string) => {
    const optNumber = value?.replace(/[^0-9]/g, '');
    setValue('otp1', optNumber.charAt(0) || otp1);
    setValue('otp2', optNumber.charAt(1) || otp2);
    setValue('otp3', optNumber.charAt(2) || otp3);
    setValue('otp4', optNumber.charAt(3) || otp4);
  };

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          setStep(1);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-2.header.title')}
          desc={t('step-2.header.desc')}
        />
        <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Row className={styles.mb24}>
            <Col span={24} className={styles.confirmationCodeWrapper}>
              <InputV2
                type="number"
                isInvalid={!!showError}
                {...register('otp1')}
                disabled={isSubmitting}
                autoComplete="off"
                onChange={(event) => {
                  const numberValue = event.target.value.replace(
                    /[^0-9]/g,
                    ''
                  )[0];
                  setValue('otp1', numberValue);
                  numberValue && setFocus('otp2');
                }}
                onPaste={(event) => {
                  event.preventDefault();
                  const value = event.clipboardData.getData('Text');
                  fillAllOtp(value);
                  return false;
                }}
              />
              <InputV2
                type="number"
                isInvalid={!!showError}
                {...register('otp2')}
                disabled={isSubmitting}
                autoComplete="off"
                onChange={(event) => {
                  const numberValue = event.target.value.replace(
                    /[^0-9]/g,
                    ''
                  )[0];
                  setValue('otp2', numberValue);
                  numberValue && setFocus('otp3');
                }}
                onPaste={(event) => {
                  event.preventDefault();
                  const value = event.clipboardData.getData('Text');
                  fillAllOtp(value);
                  return false;
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace' && !otp2) {
                    setFocus('otp1');
                  }
                }}
              />
              <InputV2
                type="number"
                isInvalid={!!showError}
                {...register('otp3')}
                disabled={isSubmitting}
                autoComplete="off"
                onChange={(event) => {
                  const numberValue = event.target.value.replace(
                    /[^0-9]/g,
                    ''
                  )[0];
                  setValue('otp3', numberValue);
                  numberValue && setFocus('otp4');
                }}
                onPaste={(event) => {
                  event.preventDefault();
                  const value = event.clipboardData.getData('Text');
                  fillAllOtp(value);
                  return false;
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace' && !otp3) {
                    setFocus('otp2');
                  }
                }}
              />
              <InputV2
                type="number"
                isInvalid={!!showError}
                {...register('otp4')}
                disabled={isSubmitting}
                autoComplete="off"
                onChange={(event) => {
                  const numberValue = event.target.value.replace(
                    /[^0-9]/g,
                    ''
                  )[0];
                  setValue('otp4', numberValue);
                }}
                onPaste={(event) => {
                  event.preventDefault();
                  const value = event.clipboardData.getData('Text');
                  fillAllOtp(value);
                  return false;
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace' && !otp4) {
                    setFocus('otp3');
                  }
                }}
              />
            </Col>
          </Row>

          {showError && <div className={styles.errorMessage}>{showError}</div>}
          <p className={styles.message}>{t('step-2.message')}</p>

          <div className={styles.timerContainer}>
            <p className={styles.timer}>
              00:{seconds < 10 ? `0${seconds}` : seconds}
            </p>
            <p className={styles.dot}>•</p>
            <p
              className={cn(styles.resendButton, {
                [styles.disabled]: !!seconds,
              })}
              onClick={handleReset}
            >
              {t('step-2.resend-code')}
            </p>
          </div>
        </Form>
      </div>
    </>
  );
};
